import PropTypes from 'prop-types';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  FileText,
  AlertCircle,
  User,
  Shield,
  Building2,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import AppLayout from '@components/AppLayout';
import client from '../../src/api/client';
import { createTicketSocket, joinTicketRoom, leaveTicketRoom } from '../../src/realtime/ticketSocket';
import '../styles/ticket-detail.css';

const STATUS_META = {
  open: { label: 'Open', tone: 'pending' },
  pending: { label: 'Pending', tone: 'pending' },
  ticket_assigned: { label: 'Ticket Assigned', tone: 'assigned' },
  in_progress: { label: 'In Progress', tone: 'progress' },
  closed: { label: 'Closed', tone: 'resolved' },
  rejected: { label: 'Rejected', tone: 'rejected' },
  deleted: { label: 'Deleted', tone: 'deleted' },
};

const STATUS_STEPS = ['open', 'ticket_assigned', 'in_progress', 'closed'];

const DEPARTMENT_ROLE_LABELS = {
  legal: 'Legal Department',
  medical: 'Medical Department',
  education: 'Education Department',
  emergency: 'Emergency Support',
  finance: 'Finance Department',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatShortDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toTitleCase = (value) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const getViewerName = (user) => {
  if (!user) return 'You';
  const fullName = [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return user.name || user.fullName || user.email || 'You';
};

const normalizeSenderRole = (message) => {
  const rawRole = message?.senderRole || message?.sender_role || message?.authorType;
  if (!rawRole) {
    if (message?.authorDeptAccountId || message?.authorDeptId || message?.isDept) return 'department';
    if (message?.authorAdminId || message?.isAdmin) return 'admin';
    if (message?.authorUserId || message?.isUser) return 'user';
    return 'user';
  }
  const role = String(rawRole).toLowerCase();
  if (role === 'dept') return 'department';
  if (role.includes('department')) return 'department';
  if (role.includes('admin')) return 'admin';
  if (role.includes('user') || role.includes('member') || role === 'client') return 'user';
  return 'user';
};

const getUserFacingAdminLabel = (role) => {
  if (!role) return 'Ticket Admin';
  const normalized = String(role).toLowerCase();
  if (normalized.includes('super')) return 'Super Admin';
  if (normalized.includes('ticket')) return 'Ticket Admin';
  return 'Ticket Admin';
};

const getAdminRoleTone = (role) => {
  if (!role) return 'default';
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes('super')) return 'super';
  if (normalized.includes('ticket')) return 'ticket';
  return 'default';
};

const getUserFacingDepartmentLabel = (department, fallbackDepartment) => {
  const normalized = String(department || fallbackDepartment || '').toLowerCase().trim();
  if (!normalized) return 'Department';
  return DEPARTMENT_ROLE_LABELS[normalized] || toTitleCase(normalized);
};

export default function TicketDetailPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { ticketId } = useParams();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');

  const messagesContainerRef = useRef(null);
  const socketRef = useRef(null);

  const loadTicket = useCallback(async () => {
    if (!user?.id || !ticketId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await client.get(`/api/users/${user.id}/tickets/${ticketId}/messages`);
      setTicket(res.data.ticket || null);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Error loading ticket:', err);
      setError('Unable to load ticket details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, ticketId]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      await loadTicket();
      if (!active) return;
    };
    run();
    return () => { active = false; };
  }, [loadTicket]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const activeTicketId = ticket?.id ?? ticketId;
    if (!activeTicketId || !user?.id) {
      return undefined;
    }

    const socket = createTicketSocket('member');
    if (!socket) {
      return undefined;
    }

    socketRef.current = socket;

    const handleReceiveMessage = (incomingMessage) => {
      if (String(incomingMessage?.ticketId ?? '') !== String(activeTicketId)) {
        return;
      }

      setMessages((previousMessages) => {
        const alreadyExists = previousMessages.some((message) => String(message.id) === String(incomingMessage.id));
        if (alreadyExists) {
          return previousMessages;
        }
        return [...previousMessages, incomingMessage];
      });
    };

    const joinCurrentTicketRoom = () => {
      joinTicketRoom(socket, activeTicketId);
    };

    socket.on('connect', joinCurrentTicketRoom);
    socket.on('receive_message', handleReceiveMessage);
    joinCurrentTicketRoom();

    return () => {
      leaveTicketRoom(socket, activeTicketId);
      socket.off('connect', joinCurrentTicketRoom);
      socket.off('receive_message', handleReceiveMessage);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [ticket?.id, ticketId, user?.id]);

  const handleSendReply = async () => {
    const trimmed = replyText.trim();
    const targetTicketId = ticket?.id ?? ticketId;
    if (!trimmed || !user?.id || !targetTicketId) return;

    setReplySending(true);
    setReplyError('');
    try {
      await client.post(`/api/users/${user.id}/tickets/${targetTicketId}/messages`, { message: trimmed });
      setReplyText('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setReplyError('Failed to send message. Please try again.');
    } finally {
      setReplySending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const statusMeta = STATUS_META[ticket?.status] || STATUS_META.pending;
  const isClosed = ['closed', 'rejected', 'deleted'].includes(ticket?.status);

  const currentStepIndex = STATUS_STEPS.indexOf(ticket?.status ?? '');

  return (
    <AppLayout title="Ticket Details" user={user} onLogout={onLogout}>
      <div className="ticket-detail-page">
        <div className="ticket-detail__top-actions">
          <button
            type="button"
            className="ticket-detail__header-button"
            onClick={() => navigate('/my-requests')}
          >
            <ArrowLeft size={18} />
            Back to My Requests
          </button>
        </div>

        <div className="ticket-detail__content-layout">
          <div className="ticket-detail__main-column">

            {loading && (
              <div className="ticket-detail__state">
                <div className="ticket-detail__spinner" />
                <p>Loading ticket details...</p>
              </div>
            )}

            {error && !loading && (
              <div className="ticket-detail__state ticket-detail__state--error">
                <AlertCircle size={24} />
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && !ticket && (
              <div className="ticket-detail__state">
                <FileText size={24} />
                <p>Ticket not found.</p>
              </div>
            )}

            {!loading && !error && ticket && (
              <>
                {/* Ticket Header */}
                <header className="ticket-detail__header">
                  <div className="ticket-detail__header-left">
                    <span className="ticket-detail__ticket-no">{ticket.ticketNo || `#${ticket.id}`}</span>
                    <h2 className="ticket-detail__subject">{ticket.subject}</h2>
                  </div>
                  <span className={`ticket-detail__badge ticket-detail__badge--${statusMeta.tone}`}>
                    {statusMeta.label}
                  </span>
                </header>

                {/* Status Timeline */}
                {currentStepIndex >= 0 && (
                  <div className="ticket-detail__timeline">
                    {STATUS_STEPS.map((step, index) => {
                      const isCompleted = index <= currentStepIndex;
                      const isCurrent = index === currentStepIndex;
                      return (
                        <div
                          key={step}
                          className={`ticket-detail__timeline-step${isCompleted ? ' is-completed' : ''}${isCurrent ? ' is-current' : ''}`}
                        >
                          <div className="ticket-detail__timeline-dot">
                            {isCompleted && <CheckCircle2 size={16} />}
                          </div>
                          <span className="ticket-detail__timeline-label">{toTitleCase(step)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Ticket Info Grid */}
                <div className="ticket-detail__info-grid">
                  <div className="ticket-detail__info-item">
                    <span className="ticket-detail__info-label">Category</span>
                    <span className="ticket-detail__info-value">{toTitleCase(ticket.category)}</span>
                  </div>
                  <div className="ticket-detail__info-item">
                    <span className="ticket-detail__info-label">Priority</span>
                    <span className="ticket-detail__info-value">{toTitleCase(ticket.priority)}</span>
                  </div>
                  {ticket.department && (
                    <div className="ticket-detail__info-item">
                      <span className="ticket-detail__info-label">Department</span>
                      <span className="ticket-detail__info-value">{ticket.department}</span>
                    </div>
                  )}
                  <div className="ticket-detail__info-item">
                    <span className="ticket-detail__info-label">Created</span>
                    <span className="ticket-detail__info-value">{formatShortDate(ticket.createdAt)}</span>
                  </div>
                  {ticket.slaDeadline && (
                    <div className="ticket-detail__info-item">
                      <span className="ticket-detail__info-label">
                        <Clock size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        SLA Deadline
                      </span>
                      <span className="ticket-detail__info-value">{formatShortDate(ticket.slaDeadline)}</span>
                    </div>
                  )}
                </div>

                {/* Conversation */}
                <section className="ticket-detail__conversation">

                  <div className="ticket-detail__messages" ref={messagesContainerRef}>
                    {messages.length === 0 && (
                      <div className="ticket-detail__empty-messages">
                        <p>No messages yet. Your request is being reviewed.</p>
                      </div>
                    )}

                    {messages.map((m) => {
                      const senderRole = normalizeSenderRole(m);
                      const isDept = senderRole === 'department';
                      const isAdmin = senderRole === 'admin';
                      const senderType = isDept ? 'dept' : isAdmin ? 'admin' : 'user';
                      const isOutgoing = senderType === 'user';
                      const viewerName = getViewerName(user);

                      const senderLabel = isDept
                        ? getUserFacingDepartmentLabel(m.authorDeptDepartment, ticket.department)
                        : isAdmin
                          ? getUserFacingAdminLabel(m.authorAdminRole)
                          : (m.authorName || viewerName || 'You');

                      return (
                        <div
                          key={m.id}
                          className={`ticket-detail__message ticket-detail__message--${senderType}${isOutgoing ? ' ticket-detail__message--outgoing' : ''}`}
                        >
                          <div
                            className={`ticket-detail__message-avatar ticket-detail__message-avatar--${senderType}${isAdmin ? ` ticket-detail__message-avatar--admin-${getAdminRoleTone(m.authorAdminRole)}` : ''
                              }`}
                          >
                            {isDept ? <Building2 size={14} /> : isAdmin ? <Shield size={14} /> : <User size={14} />}
                          </div>
                          <div
                            className={`ticket-detail__message-body ticket-detail__message-body--${senderType}${isAdmin ? ` ticket-detail__message-body--admin-${getAdminRoleTone(m.authorAdminRole)}` : ''
                              }`}
                          >
                            <div className="ticket-detail__message-meta">
                              <strong
                                className={`ticket-detail__message-author ticket-detail__message-author--${senderType}${isAdmin ? ` ticket-detail__message-author--admin-${getAdminRoleTone(m.authorAdminRole)}` : ''
                                  }`}
                              >
                                {senderLabel}
                              </strong>
                              <span className="ticket-detail__message-time">{formatDate(m.createdAt)}</span>
                            </div>
                            <div className="ticket-detail__message-text">{m.message}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply Composer */}
                  {!isClosed ? (
                    <div className="ticket-detail__composer">
                      {replyError && (
                        <div className="ticket-detail__reply-error">
                          <AlertCircle size={16} />
                          {replyError}
                        </div>
                      )}
                      <div className="ticket-detail__composer-row">
                        <textarea
                          className="ticket-detail__textarea"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                          disabled={replySending}
                          rows={3}
                        />
                        <button
                          type="button"
                          className="ticket-detail__send-button"
                          onClick={handleSendReply}
                          disabled={replySending || !replyText.trim()}
                        >
                          <Send size={18} />
                          {replySending ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ticket-detail__closed-notice">
                      <CheckCircle2 size={18} />
                      This ticket is {ticket.status}. If you need further help, please submit a new request.
                    </div>
                  )}
                </section>
              </>
            )}
          </div>

          <aside className="ticket-detail__side-container" aria-label="Additional content panel">
            <h3>What Happens Next?</h3>
            <ul className="ticket-detail__side-list">
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                <span>Follow status updates and replies as your ticket progresses.</span>
              </li>
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                <span>Use the conversation section to add clarifications when needed.</span>
              </li>
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                <span>Once resolved or closed, you can submit a new request for new concerns.</span>
              </li>
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                <span>Return to My Requests anytime to view your full request history.</span>
              </li>
            </ul>
          </aside>
        </div>
      </div>
    </AppLayout>
  );
}

TicketDetailPage.propTypes = {
  user: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
};
